import * as z from "zod";

/** Lets the UI distinguish a real zero from a source that could not be reached. */
export const hooksBlockStatusSchema = z.enum(["ok", "unavailable"]);

/** String values match RedoInternalProduct; plain literals keep the SDK dep-free. */
export const HOOK_PRODUCT_IDS = ["AEO", "ORDER_EDITING", "RECLAIM"] as const;
export const hookProductIdSchema = z.enum(HOOK_PRODUCT_IDS);
export type HookProductId = z.infer<typeof hookProductIdSchema>;

export const hooksPerHookAdoptionSchema = z.object({
  /** One of HOOK_PRODUCT_IDS; plain string keeps enum types out of the SDK. */
  hook: z.string(),
  enabled: z.number(),
  active: z.number(),
  /** Active on this hook while paying for no non-hook product. */
  hooksOnly: z.number(),
});

export const hooksCohortTeamSchema = z.object({
  teamId: z.string(),
  name: z.string(),
});

export const hooksNewMerchantSchema = z.object({
  teamId: z.string(),
  name: z.string(),
  storeUrl: z.string().nullable(),
  /** The hook this merchant first ever activated; one of HOOK_PRODUCT_IDS. */
  firstHook: z.string(),
  firstActivatedAt: z.date(),
  /** Date came from the trial-end estimate, not a real install stamp. */
  activationInferred: z.boolean(),
  /** dealSizing.totalAnnualOrderVolume; null when never computed. */
  annualOrderVolume: z.number().nullable(),
  /** Non-hook products this merchant is active on. Empty means hooks-only. */
  alsoUses: z.array(z.string()),
  /** Billing-active on any non-hook product. */
  paysForNonHook: z.boolean(),
});

export const getHooksOverview = {
  name: "getHooksOverview" as const,
  input: z.object({
    startDate: z.date(),
    endDate: z.date(),
    /** Empty means all hooks. Scopes every block, not just the chart. */
    hooks: z.array(hookProductIdSchema).default([]),
    /** Set by the Refresh button; recomputes instead of serving the cache. */
    bypassCache: z.boolean().default(false),
  }),
  output: z.object({
    /** Point-in-time state from the nightly product sync; fatal when down. */
    adoption: z.object({
      status: hooksBlockStatusSchema,
      perHook: z.array(hooksPerHookAdoptionSchema),
      enabledOnAnyHook: z.number(),
      activeOnAnyHook: z.number(),
      hooksOnly: z.object({
        count: z.number(),
        /** Share of active hook users that pay for nothing outside hooks. */
        pctOfActiveHookUsers: z.number().nullable(),
        /** Subset with no billing on any product, hooks included. */
        fullyFree: z.number(),
        teams: z.array(hooksCohortTeamSchema),
        truncated: z.boolean(),
      }),
    }),
    /** Activations bucketed by month over the requested window. */
    installs: z.object({
      status: hooksBlockStatusSchema,
      months: z.array(z.string()),
      series: z.array(
        z.object({ hook: z.string(), counts: z.array(z.number()) }),
      ),
      /** Order editing series is first USE (no install stamp exists). */
      orderEditingTruncated: z.boolean(),
    }),
    /** Merchants whose first-ever hook activation landed in the window. */
    newMerchants: z.object({
      status: hooksBlockStatusSchema,
      count: z.number(),
      /** Excludes merchants whose activation date was inferred, not stamped. */
      stampedCount: z.number(),
      days: z.array(z.string()),
      counts: z.array(z.number()),
      /** Server-owned so the goal is defined once, not forked into the UI. */
      targetPerDay: z.number(),
      averagePerDay: z.number().nullable(),
      /** Complete UTC days behind averagePerDay; excludes today. */
      completeDayCount: z.number(),
      opportunity: z.object({
        totalAnnualOrderVolume: z.number(),
        /** The total covers only these merchants; the rest have no estimate. */
        coveredCount: z.number(),
      }),
      /** "using" is isActive, "paying" is isBillingActive — different sets. */
      crossSell: z.object({ usingCount: z.number(), payingCount: z.number() }),
      merchants: z.array(hooksNewMerchantSchema),
      truncated: z.boolean(),
    }),
    /** Merchants whose first-install product choice was a hook. */
    hookFirst: z.object({
      status: hooksBlockStatusSchema,
      perProduct: z.array(z.object({ product: z.string(), count: z.number() })),
      reclaimInstallSourceCount: z.number(),
      /** Hook-first teams now paying for a non-hook product. */
      convertedCount: z.number(),
      conversionRatePct: z.number().nullable(),
    }),
  }),
};

export type GetHooksOverviewOutput = z.infer<typeof getHooksOverview.output>;
